import config from "../Configurations";
import firebase from "FirebaseConfig.js";

export function industryToSectorSlug(industryName) {
  const key = String(industryName || "").trim().toLowerCase();
  if (!key || key === "others") return "hospitality";
  if (key.includes("hospitality") || key.includes("hotel") || key.includes("otel")) return "hospitality";
  if (key.includes("telecom") || key.includes("telekom")) return "telecommunications";
  if (key.includes("retail") || key.includes("perakende")) return "retail";
  if (key.includes("bank") || key.includes("finans") || key.includes("financial")) return "banking";
  const slug = key.replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  return slug || "hospitality";
}

export function resolveSonicWsBaseUrl() {
  if (config.SONIC_WS_URL) return String(config.SONIC_WS_URL).replace(/\/$/, "");
  if (typeof window !== "undefined") {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}/sonic/live/ws`;
  }
  return "ws://127.0.0.1:8080/live/ws";
}

export async function buildSonicLiveWsUrl({
  sessionId,
  sector,
  languageCode,
  voiceEngine,
  elevenlabsVoiceId,
}) {
  const base = resolveSonicWsBaseUrl();
  const params = new URLSearchParams();
  const token = await firebase.auth().currentUser?.getIdToken();
  if (token) params.set("token", token);
  if (sessionId) params.set("session_id", sessionId);
  params.set("sector", sector || "hospitality");
  params.set("language_code", languageCode || "tr-TR");
  if (voiceEngine) params.set("voice_engine", voiceEngine);
  if (elevenlabsVoiceId) params.set("elevenlabs_voice_id", elevenlabsVoiceId);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Mirror backend sonic_repo.resolve_voice_engine for UI gating. */
export function resolveVoiceEngine(voiceCfg = {}) {
  const explicit = (voiceCfg.voice_engine || "auto").trim().toLowerCase();
  if (explicit === "elevenlabs") return "elevenlabs";
  if (explicit === "gemini_live") return "gemini_live";
  const lang = (voiceCfg.language || "en-US").trim().toLowerCase();
  if (lang.startsWith("tr")) return "elevenlabs";
  if (lang.startsWith("en")) return "gemini_live";
  return "elevenlabs";
}

export function isMpegAudioMime(mimeType) {
  const mime = (mimeType || "").toLowerCase();
  return mime.includes("mpeg") || mime.includes("mp3");
}

export async function playMpegBase64(b64, { onStart, onEnd, onError } = {}) {
  const bytes = base64ToBytes(b64);
  const blob = new Blob([bytes], { type: "audio/mpeg" });
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  if (onStart) onStart(audio);
  try {
    await audio.play();
  } catch (err) {
    URL.revokeObjectURL(url);
    if (onError) onError(err);
    throw err;
  }
  return new Promise((resolve, reject) => {
    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (onEnd) onEnd();
      resolve(audio.duration || 0);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      const err = new Error("playback");
      if (onError) onError(err);
      reject(err);
    };
  });
}

export function base64FromInt16(int16) {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function downsampleTo16k(float32, inputRate) {
  const target = 16000;
  if (inputRate === target) {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i += 1) {
      const s = Math.max(-1, Math.min(1, float32[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }
  const ratio = inputRate / target;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const idx = Math.floor(i * ratio);
    const s = Math.max(-1, Math.min(1, float32[idx]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

export function parsePcmSampleRate(mimeType) {
  const match = /rate=(\d+)/.exec(mimeType || "");
  return match ? parseInt(match[1], 10) : 24000;
}
